import { createStore } from 'zustand'

import { DEFAULT_AGENT } from '../../types'
import {
  loadDiagnosticsEnabled,
  loadSessionFilter,
  loadSidebarWidth,
} from '../../utils/chatPrefs'
import { prefsMutations } from './prefs'
import { sessionsLoaders, sessionsMutations } from './sessions'
import { streamMutations } from './stream'
import type { ChatStore } from './types'

export type {
  AgentStatus,
  ChatStore,
  PendingFilterAction,
  SessionFilter,
} from './types'

export const createChatStore = () =>
  createStore<ChatStore>((set, get, store) => ({
    loadingSessions: false,
    loadingMessages: false,
    loadingArchived: false,
    sendingMessage: false,
    sessions: [],
    archivedSessions: [],
    currentSessionId: null,
    messages: [],
    cancelledReportEditTokens: [],
    optimisticUserText: null,
    optimisticUserSessionId: null,
    draftAssistant: null,
    pendingFilters: null,
    pendingSeed: null,
    thinkingSince: null,
    draftingTool: null,
    agentStatus: null,
    lastStreamEventAt: 0,
    // Fixed to the only agent. There is no user-facing agent picker;
    // the top-level agent is chosen server-side (and will eventually
    // delegate to subagents itself).
    selectedAgent: DEFAULT_AGENT,
    diagnosticsEnabled: loadDiagnosticsEnabled(),
    // Starts collapsed; `useHydrateSidebarCollapsed` restores the per-project
    // expanded state from session storage right after mount so it survives a
    // refresh. Persistence is keyed by project slug (not global), so expanding
    // chat in one project never auto-opens it in another.
    sidebarCollapsed: true,
    sidebarWidth: loadSidebarWidth(),
    sidebarFullScreen: false,
    historyPopoverOpen: false,
    sessionFilter: loadSessionFilter(),
    sessionSearch: '',
    // Hidden until `useChatIntro` hydrates the per-user dismissed flag, so the
    // nudge never flashes for someone who already dismissed it.
    chatIntroDismissed: true,

    loaders: {
      ...sessionsLoaders(set, get, store),
    },

    mutations: {
      ...sessionsMutations(set, get, store),
      ...streamMutations(set, get, store),
      ...prefsMutations(set, get, store),
    },
  }))
